import React from 'react'
import { useApp } from '../context/AppContext'
import { api } from '../api/client'
import { downloadFile } from '../api/download'
import { screenCache } from '../api/screenCache'
import type { Earnings as EarningsData, Expense, PaymentMethod } from '../types/app'
import { PAYMENT_METHODS, REPORT_METHODS } from '../payments/methods'
import { formatDay } from '../catalog/format'
import { todayIso } from '../calendar/dates'
import { saveErrorMessage, showToast } from './settings/toast'
import { CATEGORY_LABELS } from './earnings/expenseForm'
import ExpenseSheet from './earnings/ExpenseSheet'
import TaxCard from './earnings/TaxCard'
import ProLock from '../billing/ProLock'
import { HINT_EARNINGS } from '../billing/rungs'
import { useBilling } from '../billing/useBilling'
import { earningsDraft } from './earnings/earningsDraft'
import {
  money,
  monthLabel,
  nextMonth,
  prevMonth,
  share,
  taxYearFor,
  taxYearLabel,
  visibleServiceLines,
  estimate,
} from './earnings/format'
import './Earnings.css'
import './settings/Settings.css'
import { Icon, IconButton } from '../ui/Icon'
import { Loading } from '../ui/States'

function methodLine(byMethod: Record<string, number>): string {
  return REPORT_METHODS.map(({ method, label }) => ({ label, pence: byMethod[method] ?? 0 }))
    .filter(({ pence }) => pence > 0)
    .map(({ label, pence }) => `${label} ${money(pence)}`)
    .join(' · ')
}

export default function Earnings() {
  const { dispatch } = useApp()
  // Take-home, the tax reserve, by treatment, the goal line and the month's
  // summary are Pro's. Whether they show is her plan's say, never what a
  // cached month happens to carry (she may have been on Pro when it was
  // cached).
  const { coachLocked } = useBilling()
  // earningsDraft already remembers which month this session was looking at
  // (it outlives the screen unmounting on tab switch), so that's the key a
  // cached page can be shown under before the fetch below confirms it.
  const cachedMonth = earningsDraft.month()
  const cached = cachedMonth ? screenCache.get<EarningsData>(cachedMonth) : undefined
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>(() => (cached ? 'ready' : 'loading'))
  const [earnings, setEarnings] = React.useState<EarningsData | null>(() => cached ?? null)
  const [payingOwedId, setPayingOwedId] = React.useState<string | null>(null)
  const [owedOpen, setOwedOpen] = React.useState(false)
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [editingExpense, setEditingExpense] = React.useState<Expense | null>(null)
  const [taxYear, setTaxYear] = React.useState<number | null>(null)
  const [downloadingCsv, setDownloadingCsv] = React.useState(false)
  const today = todayIso()
  const latest = React.useRef(0)

  // A cached month is shown at once, and a failed background refresh just
  // keeps it rather than swap to the error screen. A request token guards
  // against a slow response for a month she's since navigated away from
  // (rapid prev/next taps) landing after a newer one and showing stale data.
  const load = React.useCallback(async (month?: string) => {
    const request = ++latest.current
    const key = month ?? earningsDraft.month() ?? undefined
    const cachedForKey = key ? screenCache.get<EarningsData>(key) : undefined
    if (cachedForKey) {
      setEarnings(cachedForKey)
      setStatus('ready')
    } else {
      setStatus('loading')
    }
    try {
      const data = await api.getEarnings(month)
      // The cache write happens for any response, even a stale one: it's
      // still a valid snapshot of that month, just not necessarily the one
      // on screen right now.
      screenCache.set(data.month, data)
      if (request === latest.current) {
        setEarnings(data)
        earningsDraft.saveMonth(data.month)
        setTaxYear((current) => current ?? taxYearFor(today))
        setStatus('ready')
      }
    } catch {
      if (request === latest.current && !cachedForKey) setStatus('error')
    }
  }, [today])

  React.useEffect(() => {
    load(earningsDraft.month() ?? undefined)
    // Only on mount: later navigation calls load() directly with an explicit month.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openGoals = () => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'goals' })

  const markPaid = async (appointmentId: string, method: PaymentMethod) => {
    setPayingOwedId(null)
    try {
      await api.recordPayment(appointmentId, method)
      showToast(dispatch, 'Marked as paid')
      if (earnings) load(earnings.month)
    } catch (e) {
      showToast(dispatch, saveErrorMessage(e))
    }
  }

  const openNewExpense = () => {
    setEditingExpense(null)
    setSheetOpen(true)
  }

  const openEditExpense = (expense: Expense) => {
    setEditingExpense(expense)
    setSheetOpen(true)
  }

  const onExpenseSaved = () => {
    setSheetOpen(false)
    if (earnings) load(earnings.month)
  }

  const downloadAccountantCsv = async () => {
    if (taxYear === null) return
    setDownloadingCsv(true)
    try {
      await downloadFile(
        api.exportUrl(`/v1/exports/accountant?tax_year=${taxYear}`),
        `harmovi-earnings-${taxYear}-${String((taxYear + 1) % 100).padStart(2, '0')}.csv`,
      )
    } catch {
      showToast(dispatch, "Couldn’t download. Try again.")
    } finally {
      setDownloadingCsv(false)
    }
  }

  const renderOwed = () =>
    earnings && earnings.owed.length > 0 ? (
      <section className="earnings-section">
        {/* One line until she wants the list (design review P3). */}
        <button type="button" className="earnings-owed-summary" aria-expanded={owedOpen} onClick={() => setOwedOpen(!owedOpen)}>
          <span>
            <span className="earnings-owed-summary__title">To pay (all time)</span>
            <span className="earnings-owed-summary__meta">
              {earnings.owed.length} {earnings.owed.length === 1 ? 'appointment' : 'appointments'} · {money(earnings.owed.reduce((sum, item) => sum + item.amount_pence, 0))}
            </span>
          </span>
          <span className="earnings-owed-summary__go">{owedOpen ? 'Hide' : 'Review'}</span>
        </button>
        {owedOpen && <ul className="earnings-owed-list">
          {earnings.owed.map((item) => (
            <li key={item.appointment_id} className="earnings-owed-item">
              <div className="earnings-owed-item__main">
                <span className="earnings-owed-item__name">{item.client_name}</span>
                <span className="earnings-owed-item__meta">
                  {formatDay(item.local_date)} · {money(item.amount_pence)}
                </span>
              </div>
              {payingOwedId === item.appointment_id ? (
                <div className="earnings-owed-item__methods">
                  {PAYMENT_METHODS.map(({ method, label }) => (
                    <button
                      key={method}
                      type="button"
                      className="btn btn-secondary btn-compact"
                      onClick={() => markPaid(item.appointment_id, method)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : (
                <button type="button" className="btn btn-secondary btn-compact" onClick={() => setPayingOwedId(item.appointment_id)}>
                  Mark paid
                </button>
              )}
            </li>
          ))}
        </ul>}
      </section>
    ) : null

  if (status !== 'ready' || !earnings) {
    return (
      <div className="screen earnings-screen">
        <h1>Earnings</h1>
        {status === 'error' ? (
          <>
            <p className="goals-muted">Couldn’t load your earnings. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={() => load()}>
              Try again
            </button>
          </>
        ) : (
          <Loading />
        )}
      </div>
    )
  }

  if (earnings.empty) {
    return (
      <div className="screen earnings-screen">
        <h1>Earnings</h1>
        <p className="goals-muted">
          Your earnings build from your first payment. Mark an appointment paid, or add an expense.
        </p>
        <button type="button" className="btn btn-secondary btn-compact earnings-add-expense" onClick={openNewExpense}>
          <Icon name="plus" size={16} />
          Add expense
        </button>
        {renderOwed()}
        {sheetOpen && (
          <ExpenseSheet expense={editingExpense} today={today} onClose={() => setSheetOpen(false)} onSaved={onExpenseSaved} />
        )}
      </div>
    )
  }

  // Pro's parts of the month, only while she is on Pro.
  const tax = coachLocked ? null : earnings.tax
  const goal = coachLocked ? null : earnings.goal
  const byService = coachLocked ? null : earnings.by_service

  // Where it came from: by payment method on every rung (Free keeps how her
  // takings were paid), by treatment only on Pro. Off Pro it sits above the
  // lock, which holds the by-treatment placeholder.
  const whereItCameFrom =
    (earnings.revenue_pence > 0 || earnings.refunds_pence > 0 || earnings.disputes_pence > 0 || earnings.discounts_pence > 0) && (
      <section className="earnings-section">
        <h2>Where it came from</h2>
        {earnings.revenue_pence > 0 && byService && (
        <ul className="earnings-service-list">
          {visibleServiceLines(byService).map((line) => (
            <li key={line.label} className="earnings-service-row">
              <span>{line.label}</span>
              <span>
                {money(line.amount_pence)} · {share(line.share_bp)}
              </span>
            </li>
          ))}
        </ul>
        )}
        {earnings.revenue_pence > 0 && methodLine(earnings.by_method) && <p className="earnings-method-line">{methodLine(earnings.by_method)}</p>}
        {earnings.discounts_pence > 0 && <p className="earnings-method-line">Discounts given {money(earnings.discounts_pence)}</p>}
        {earnings.refunds_pence > 0 && <p className="earnings-method-line">Refunds −{money(earnings.refunds_pence)}</p>}
        {earnings.disputes_pence > 0 && <p className="earnings-method-line">Disputes lost −{money(earnings.disputes_pence)}</p>}
      </section>
    )

  const taxYearOptions: number[] = []
  for (let year = taxYearFor(today); year >= taxYearFor(earnings.first_month); year--) {
    taxYearOptions.push(year)
  }

  return (
    <div className="screen earnings-screen">
      <h1>Earnings</h1>

      <div className="earnings-month-switcher">
        <IconButton
          icon="chevron-left"
          variant="outlined"
          className="earnings-month-arrow"
          label="Previous month"
          disabled={earnings.month === earnings.first_month}
          onClick={() => load(prevMonth(earnings.month))}
        />
        <span className="earnings-month-label">{monthLabel(earnings.month)}</span>
        <IconButton
          icon="chevron-right"
          variant="outlined"
          className="earnings-month-arrow"
          label="Next month"
          disabled={earnings.is_current}
          onClick={() => load(nextMonth(earnings.month))}
        />
      </div>

      {!coachLocked && earnings.summary && <p className="earnings-summary">{earnings.summary}</p>}

      <div className="earnings-tiles">
        <div className="earnings-tile">
          <div className="earnings-tile__value earnings-tile__value--revenue">{money(earnings.revenue_pence)}</div>
          <div className="earnings-tile__label">Takings</div>
        </div>
        <div className="earnings-tile">
          <div className="earnings-tile__value">{money(earnings.expenses_pence + earnings.card_fees_pence)}</div>
          <div className="earnings-tile__label">Expenses</div>
        </div>
        {tax && (
          <>
            <div className="earnings-tile">
              <div className="earnings-tile__value">{money(earnings.profit_pence)}</div>
              <div className="earnings-tile__label">Take-home</div>
            </div>
            <div className="earnings-tile">
              <div className="earnings-tile__value">{tax.needs_region ? '—' : money(tax.set_aside_month_pence)}</div>
              <div className="earnings-tile__label">Set aside for tax</div>
            </div>
          </>
        )}
      </div>

      {/* Off Pro, what Pro works out is one locked area under the two live
          tiles and how her takings were paid (owner, 2026-10-24):
          placeholders only, never a cached Pro month's numbers. */}
      {coachLocked && whereItCameFrom}

      {coachLocked && (
        <ProLock hint={HINT_EARNINGS} lock="take_home" className="earnings-lock">
          <div className="earnings-tiles">
            <div className="earnings-tile">
              <div className="earnings-tile__value">£—</div>
              <div className="earnings-tile__label">Take-home</div>
            </div>
            <div className="earnings-tile">
              <div className="earnings-tile__value">£—</div>
              <div className="earnings-tile__label">Set aside for tax</div>
            </div>
          </div>
          <div className="earnings-lock__block">
            <h2>By treatment</h2>
            <span className="earnings-lock__bar" />
            <span className="earnings-lock__bar" />
            <span className="earnings-lock__bar" />
          </div>
          <div className="earnings-lock__row">
            <span>Freedom Score</span>
            <span>—</span>
          </div>
        </ProLock>
      )}

      {goal && (
        <div className="goal-card earnings-goal-line">
          <button type="button" className="earnings-goal-link" onClick={openGoals}>
            {money(goal.take_home_pence)} take-home of your {money(goal.target_pence)} goal <Icon name="chevron-right" size={16} />
          </button>
          {goal.costs_avg_pence !== null && (
            <p className="earnings-costs-check">
              Your costs average {money(goal.costs_avg_pence)}; your goal assumes{' '}
              {estimate(goal.costs_estimate_pence)}.{' '}
              <button type="button" className="btn btn-quiet btn-compact" onClick={openGoals}>
                Update it?
              </button>
            </p>
          )}
        </div>
      )}

      {renderOwed()}

      {!coachLocked && whereItCameFrom}

      <section className="earnings-section">
        <div className="earnings-section__head">
          <h2>Expenses</h2>
          <button type="button" className="btn btn-quiet btn-compact" onClick={openNewExpense}>
            <Icon name="plus" size={16} />
            Add expense
          </button>
        </div>
        <ul className="earnings-expense-list">
          {earnings.card_fees_pence > 0 && (
            <li className="earnings-expense-row earnings-expense-row--readonly">
              <span className="earnings-expense-row__main">
                <span className="earnings-expense-row__category">Card fees</span>
              </span>
              <span className="earnings-expense-row__amount">{money(earnings.card_fees_pence)}</span>
            </li>
          )}
          {earnings.expenses.map((expense) => (
            <li key={expense.id} className="earnings-expense-row">
              <button type="button" className="earnings-expense-row__main" onClick={() => openEditExpense(expense)}>
                <span className="earnings-expense-row__category">{CATEGORY_LABELS[expense.category]}</span>
                {expense.note && <span className="earnings-expense-row__note">{expense.note}</span>}
                <span className="earnings-expense-row__date">{formatDay(expense.spent_on)}</span>
              </button>
              <span className="earnings-expense-row__amount">{money(expense.amount_pence)}</span>
            </li>
          ))}
        </ul>
      </section>

      {tax && <TaxCard tax={tax} onChanged={() => load(earnings.month)} />}

      <section className="earnings-section earnings-download">
        <h2>Download for my accountant</h2>
        <div className="earnings-download__row">
          <select
            aria-label="Tax year"
            value={taxYear ?? taxYearOptions[0]}
            onChange={(e) => setTaxYear(Number(e.target.value))}
          >
            {taxYearOptions.map((year) => (
              <option key={year} value={year}>
                {taxYearLabel(year)}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-secondary btn-compact" disabled={downloadingCsv} onClick={downloadAccountantCsv}>
            {downloadingCsv ? 'Downloading…' : 'Download for my accountant'}
          </button>
        </div>
      </section>

      {sheetOpen && (
        <ExpenseSheet expense={editingExpense} today={today} onClose={() => setSheetOpen(false)} onSaved={onExpenseSaved} />
      )}
    </div>
  )
}
